'use client'

import React, { useState, useEffect, useCallback } from 'react'
import {
  CheckCircle2,
  ShieldCheck,
  Layers,
  Smartphone,
  RefreshCw,
  Layers3,
  ArrowLeft,
} from 'lucide-react'
import { useRouter } from 'next/navigation'

export interface Project {
  id: string
  title: string
  category: string
  tagline: string
  role: string
  timeline: string
  platform: string
  problem: string
  solution: string
  research: string
  validation: string
  color: string
}

interface Props {
  proj: Project
}

const FLOWS_DATA = [
  {
    id: '1',
    title: '01. 瀏覽與分類篩選流程',
    subtitle: 'Browse & Filter',
    desc: '提供分類標籤、狀態標記與底部篩選面板，協助教職員快速尋找可用優惠券；並設計親切的空狀態提醒。',
    tag: '✓ 快速掃讀與精準篩選',
    screens: [
      { label: '優惠券列表', imgSrc: '/images/優惠券列表.svg', aspect: '9/19.5' },
      { label: '分類篩選面板', imgSrc: '/images/分類篩選面板.svg', aspect: '9/19.5' },
      { label: '無優惠券空狀態', imgSrc: '/images/空狀態.svg', aspect: '9/19.5' },
    ],
    caption: '左：優惠券列表 / 中：分類篩選面板 / 右：空狀態',
  },
  {
    id: '2',
    title: '02. 優惠券詳細頁與防誤觸機制',
    subtitle: 'Coupon Detail',
    desc: '將優惠內容、適用期限與使用細則按優先級分層呈現，並提供確認彈窗與倒數計時器，避免結帳時誤按或逾期使用。',
    tag: '✓ 預防結帳前誤點兌換',
    screens: [
      { label: '優惠券詳細頁', imgSrc: '/images/優惠券詳細頁.svg', aspect: '9/19.5' },
      { label: '兌換二次確認', imgSrc: '/images/兌換確認.svg', aspect: '9/19.5' },
    ],
    caption: '左：優惠券詳細頁 / 右：兌換二次確認',
  },
  {
    id: '3',
    title: '03. 兌換結果狀態呈現',
    subtitle: 'Redeem Result',
    desc: '成功與失敗狀態具備鮮明的視覺區隔，並引導教職員明確進行下一步操作，避免產生困惑。',
    tag: '✓ 具備清晰後續指引',
    screens: [
      { label: '兌換失敗頁面', imgSrc: '/images/兌換失敗.svg', aspect: '9/19.5' },
      { label: '兌換成功頁面', imgSrc: '/images/兌換成功.svg', aspect: '9/19.5' },
    ],
    caption: '左：兌換失敗 / 右：兌換成功',
  },
  {
    id: '4',
    title: '04. 優惠券同儕轉贈流程',
    subtitle: 'Transfer Flow',
    desc: '提供帳號查核與再次確認步驟，嚴格防護轉贈此項不可逆操作，避免誤傳給錯誤帳號。',
    tag: '✓ 防止誤轉贈至錯誤帳號',
    screens: [
      { label: '輸入受贈教職員', imgSrc: '/images/輸入受贈教師.svg', aspect: '9/19.5' },
      { label: '轉贈二次確認', imgSrc: '/images/轉贈確認.svg', aspect: '9/19.5' },
      { label: '轉贈成功頁面', imgSrc: '/images/轉贈成功.svg', aspect: '9/19.5' },
    ],
    caption: '左：輸入受贈帳號 / 中：轉贈確認 / 右：轉贈成功',
  },
  {
    id: '5',
    title: '05. 歷史紀錄與可追溯性',
    subtitle: 'History',
    desc: '時間軸與狀態標籤方便教職員隨時查閱已兌換、已過期與已轉贈的優惠券，並清楚顯示受贈者資訊以供追溯。',
    tag: '✓ 完整透明的紀錄查詢',
    screens: [
      { label: '兌換歷史紀錄', imgSrc: '/images/兌換紀錄.svg', aspect: '9/19.5' },
      { label: '轉贈歷史紀錄', imgSrc: '/images/轉贈紀錄.svg', aspect: '9/19.5' },
    ],
    caption: '左：兌換紀錄 / 右：轉贈紀錄',
  },
]

const PRINCIPLES_DATA = [
  {
    icon: <CheckCircle2 size={22} color="#f97316" />,
    title: '清晰的系統狀態',
    desc: '透過色彩、圖示與文字三重編碼，使教職員能立即識別優惠券可用狀態。',
  },
  {
    icon: <Layers size={22} color="#f97316" />,
    title: '層次分明的資訊架構',
    desc: '將主要優惠、關鍵欄位與使用條款劃分為不同優先級區塊，避免大段文字帶來的視覺疲勞。',
  },
  {
    icon: <ShieldCheck size={22} color="#f97316" />,
    title: '防誤觸安全機制',
    desc: '設計二次確認步驟與動態倒數計時，防止在實體店家結帳前誤點兌換。',
  },
  {
    icon: <Smartphone size={22} color="#f97316" />,
    title: '單手操作行動優先',
    desc: '固定底部按鍵與大觸控區，專為教職員在實體門市快速出示結帳情境打造。',
  },
  {
    icon: <RefreshCw size={22} color="#f97316" />,
    title: '不可逆操作防護',
    desc: '引入受贈者身分比對彈窗，避免同儕轉贈時因輸入錯帳號而損失權益。',
  },
  {
    icon: <Layers3 size={22} color="#f97316" />,
    title: '一致的設計系統',
    desc: '模組化卡片、狀態標籤、篩選面板與結果彈窗，確保全平台體驗一致。',
  },
]

// Lightbox Component
const Lightbox = ({
  isOpen,
  images,
  currentIndex,
  onClose,
  onNext,
  onPrev,
}: {
  isOpen: boolean
  images: { label: string; imgSrc: string }[]
  currentIndex: number
  onClose: () => void
  onNext: () => void
  onPrev: () => void
}) => {
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight' && images.length > 1) onNext()
      if (e.key === 'ArrowLeft' && images.length > 1) onPrev()
    },
    [isOpen, onClose, onNext, onPrev, images.length]
  )

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  if (!isOpen || !images || images.length === 0) return null

  const minSwipeDistance = 50

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return
    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance
    if (isLeftSwipe && images.length > 1) onNext()
    if (isRightSwipe && images.length > 1) onPrev()
  }

  const currentImg = images[currentIndex]

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        backdropFilter: 'blur(12px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <button
        onClick={onClose}
        style={{
          position: 'absolute',
          top: '24px',
          right: '24px',
          background: 'rgba(255, 255, 255, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '50%',
          color: 'white',
          width: '44px',
          height: '44px',
          fontSize: '1.5rem',
          cursor: 'pointer',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        &times;
      </button>

      {images.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onPrev()
            }}
            style={{
              position: 'absolute',
              left: '24px',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: 'white',
              fontSize: '2rem',
              cursor: 'pointer',
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            &#8249;
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onNext()
            }}
            style={{
              position: 'absolute',
              right: '24px',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: 'white',
              fontSize: '2rem',
              cursor: 'pointer',
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            &#8250;
          </button>
        </>
      )}

      <div
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          maxWidth: '90%',
          maxHeight: '90%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img
          src={currentImg.imgSrc}
          alt={currentImg.label}
          style={{
            maxWidth: '100%',
            maxHeight: '80vh',
            objectFit: 'contain',
            borderRadius: '16px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          }}
        />
        <div
          style={{
            color: 'rgba(255, 255, 255, 0.8)',
            marginTop: '1.25rem',
            fontSize: '1.05rem',
            fontWeight: 600,
            textAlign: 'center',
          }}
        >
          {currentImg.label} {images.length > 1 && `(${currentIndex + 1} / ${images.length})`}
        </div>
      </div>
    </div>
  )
}

export default function YilanTeacherPerks({ proj: _proj }: Props) {
  const router = useRouter()
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const lightboxImages: { label: string; imgSrc: string }[] = []
  const [lightboxIndex, setLightboxIndex] = useState(0)

  const closeLightbox = () => setLightboxOpen(false)
  const nextImage = () => setLightboxIndex((prev) => (prev + 1) % lightboxImages.length)
  const prevImage = () =>
    setLightboxIndex((prev) => (prev - 1 + lightboxImages.length) % lightboxImages.length)

  const metaItems = [
    { label: '專案角色', value: 'UI/UX 設計師' },
    { label: '專案時間', value: '2024' },
    { label: '設計範疇', value: ['Product Design', 'UI/UX 設計', 'APP'] },
  ]

  return (
    <div
      className="yilan-case-root"
      style={{
        backgroundColor: '#ffffff',
        color: '#111827',
        width: '100%',
        overflowX: 'hidden',
        fontFamily: 'var(--font-sans)',
      }}
    >
      <style>{`
        @media (max-width: 768px) {
          .yilan-case-root section {
            padding: 0 1.25rem !important;
            margin-top: 3.5rem !important;
            margin-bottom: 3.5rem !important;
          }
          .yilan-case-root section.yilan-fullbleed-section {
            padding: 4rem 1.25rem 3.5rem !important;
            margin-top: 3.5rem !important;
          }
          .yilan-case-root section.yilan-flow-section {
            margin-bottom: 0 !important;
            padding-bottom: 3.5rem !important;
          }
          .yilan-case-root section.yilan-finale-section {
            margin-top: 0 !important;
            margin-bottom: 0 !important;
            padding: 4rem 1.25rem 4rem !important;
          }
          .yilan-case-root h2 {
            font-size: 1.5rem !important;
            margin-bottom: 1rem !important;
          }
          .yilan-hero-container {
            padding: 8.5rem 1.25rem 11rem !important;
            min-height: 640px !important;
          }
          .yilan-hero-container h1 {
            font-size: clamp(1.6rem, 5.5vw, 2.2rem) !important;
          }
          .yilan-hero-container p {
            font-size: 0.95rem !important;
            margin-bottom: 2rem !important;
          }
          .yilan-meta-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 0.85rem !important;
            padding: 1.15rem 1rem !important;
          }
          .yilan-meta-grid .yilan-meta-tags {
            grid-column: span 2 !important;
          }
          .yilan-dark-cards-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 0.85rem !important;
          }
          .yilan-dark-cards-grid > div {
            padding: 1.15rem 0.85rem !important;
            text-align: center !important;
            box-sizing: border-box !important;
          }
          .yilan-dark-cards-grid > div > div:first-child {
            font-size: clamp(1.8rem, 6vw, 2.3rem) !important;
            margin-bottom: 0.4rem !important;
          }
          .yilan-dark-cards-grid > div > h3 {
            font-size: 0.95rem !important;
            margin-bottom: 0.35rem !important;
          }
          .yilan-dark-cards-grid > div > p {
            font-size: 0.775rem !important;
            line-height: 1.45 !important;
          }
          .yilan-dark-cards-grid > div:last-child {
            grid-column: span 2 !important;
            max-width: 320px !important;
            margin: 0 auto !important;
            width: 100% !important;
          }
          .yilan-principles-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 0.85rem !important;
          }
          .yilan-principles-grid > div {
            padding: 1rem 0.85rem !important;
            flex-direction: column !important;
            align-items: center !important;
            text-align: center !important;
            gap: 0.75rem !important;
          }
          .yilan-flow-card {
            background-color: transparent !important;
            border: none !important;
            box-shadow: none !important;
            padding: 0 0 2.5rem !important;
            border-bottom: 1px solid #cbd5e1 !important;
            border-radius: 0 !important;
          }
          .yilan-flow-card:last-child {
            border-bottom: none !important;
          }
          .yilan-flow-header {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 0.5rem !important;
          }
          .yilan-flow-header h3 {
            font-size: 1.25rem !important;
          }
          .yilan-screens-scroll-container {
            flex-wrap: nowrap !important;
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch !important;
            justify-content: flex-start !important;
            gap: 1rem !important;
            padding: 0.5rem 0 1rem !important;
            width: 100vw !important;
            margin-left: calc(-50vw + 50%) !important;
            padding-left: 1.25rem !important;
            padding-right: 1.25rem !important;
            box-sizing: border-box !important;
          }
          .yilan-device-mockup {
            width: 205px !important;
            flex-shrink: 0 !important;
          }
        }
      `}</style>
      {/* 1. Integrated Hero Header with Coupon_Thumbnail Background & Shared Overlay Layout */}
      <div
        className="yilan-hero-container"
        style={{
          position: 'relative',
          width: '100%',
          padding: '10rem 2rem 12rem',
          minHeight: 'min(880px, 92vh)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#ffffff',
          overflow: 'hidden',
          backgroundColor: '#0a0a0c',
        }}
      >
        {/* Coupon_Thumbnail Background Image — full color, kept subtle via dark overlay (matches other case-study covers) */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(/images/Coupon_Thumbnail.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            opacity: 0.9,
            pointerEvents: 'none',
          }}
        />

        {/* Vertical Top-Down Gradient Overlay Fading to White (Matching Other Project Pages) — darker mid-section to blend the bright graphic edges without desaturating */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            background:
              'linear-gradient(180deg, rgba(10, 10, 13, 0.96) 0%, rgba(12, 12, 16, 0.9) 40%, rgba(15, 15, 20, 0.82) 65%, rgba(255, 255, 255, 0.92) 95%, #ffffff 100%)',
          }}
        />

        <div
          style={{
            maxWidth: '1250px',
            margin: '0 auto',
            textAlign: 'center',
            position: 'relative',
            zIndex: 2,
          }}
        >
          <h1
            style={{
              fontSize: 'clamp(2.25rem, 4vw, 3.25rem)',
              fontWeight: 800,
              color: '#ffffff',
              lineHeight: 1.25,
              marginBottom: '1.25rem',
              letterSpacing: '-0.02em',
            }}
          >
            宜蘭教育動｜教師優惠券服務設計
          </h1>
          <p
            style={{
              fontSize: '1.1rem',
              color: 'rgba(255, 255, 255, 0.88)',
              lineHeight: 1.7,
              maxWidth: '960px',
              margin: '0 auto 3rem',
            }}
          >
            為宜蘭縣教職員打造專屬的優惠券模組——涵蓋瀏覽、兌換、轉贈與歷史紀錄的全流程體驗。結合明確的狀態標示、防誤觸二次確認機制，以及親切包容的介面設計。
          </p>

          {/* Integrated Glassmorphism Meta Grid inside Hero */}
          <div
            className="yilan-meta-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '1.25rem',
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(12px)',
              borderRadius: '16px',
              padding: '1.5rem 2rem',
              textAlign: 'left',
              maxWidth: '1160px',
              margin: '0 auto',
            }}
          >
            {metaItems.map((item, idx) => (
              <div key={idx} className={Array.isArray(item.value) ? 'yilan-meta-tags' : undefined}>
                <div
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: '#818cf8',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    marginBottom: '0.35rem',
                  }}
                >
                  {item.label}
                </div>
                {Array.isArray(item.value) ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {item.value.map((tag, tagIdx) => (
                      <span
                        key={tagIdx}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          backgroundColor: 'rgba(255, 255, 255, 0.12)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          color: '#ffffff',
                          borderRadius: '99px',
                          padding: '0.3rem 0.85rem',
                          fontSize: '0.8rem',
                          fontWeight: 500,
                          lineHeight: 1.4,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div
                    style={{
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      color: '#ffffff',
                      lineHeight: 1.4,
                    }}
                  >
                    {item.value}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 3. Overview Section — Background & Challenge */}
      <section
        style={{
          maxWidth: '960px',
          margin: '5rem auto 5rem',
          padding: '0 2rem',
          textAlign: 'center',
        }}
      >
        <span
          style={{
            fontSize: '0.85rem',
            fontWeight: 700,
            color: '#4f46e5',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        >
          背景
        </span>
        <h2
          style={{
            fontSize: '2.25rem',
            fontWeight: 800,
            color: '#0f172a',
            margin: '0.5rem 0 1.5rem',
            letterSpacing: '-0.02em',
          }}
        >
          為教職員打造值得信賴的福利體驗
        </h2>
        <p style={{ fontSize: '1.15rem', color: '#334155', lineHeight: 1.8, margin: '0 auto' }}>
          在宜蘭縣教育動 APP
          中，設計專屬教職員的優惠券模組。考量公教人員平時公務繁忙，介面設計著重行動端的快速瀏覽性，優惠效期說明力求清楚明確；同時建立防誤觸機制，避免使用者在結帳前不慎按下兌換，或誤轉贈給非目標同仁，造成無法復原的操作失誤。
        </p>
      </section>

      {/* 4. Full-Bleed Feature Block — Impact Stats & Key Highlight Cards */}
      <section
        className="yilan-fullbleed-section"
        style={{
          width: '100vw',
          marginLeft: 'calc(-50vw + 50%)',
          backgroundColor: 'rgba(15, 23, 42, 0.92)',
          color: '#ffffff',
          padding: '5.5rem 2rem',
          marginBottom: '6rem',
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
            <span
              style={{
                fontSize: '0.85rem',
                fontWeight: 700,
                color: '#fb923c',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}
            >
              設計亮點
            </span>
            <h2
              style={{
                fontSize: '2.25rem',
                fontWeight: 800,
                color: '#ffffff',
                marginTop: '0.5rem',
              }}
            >
              三大核心 UX 解決方案
            </h2>
          </div>

          <div
            className="yilan-dark-cards-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '2rem',
            }}
          >
            <div style={styles.darkCard}>
              <div
                style={{
                  fontSize: '2.5rem',
                  fontWeight: 800,
                  color: '#fb923c',
                  marginBottom: '0.5rem',
                }}
              >
                01
              </div>
              <h3
                style={{
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  color: '#ffffff',
                  marginBottom: '0.5rem',
                }}
              >
                三重狀態識別
              </h3>
              <p style={{ fontSize: '0.95rem', color: '#94a3b8', margin: 0, lineHeight: 1.6 }}>
                融合專屬色彩、視覺圖示與清晰文字標籤，讓優惠券四大狀態（可使用、已兌換、已過期、已轉贈）一目了然。
              </p>
            </div>

            <div style={styles.darkCard}>
              <div
                style={{
                  fontSize: '2.5rem',
                  fontWeight: 800,
                  color: '#38bdf8',
                  marginBottom: '0.5rem',
                }}
              >
                02
              </div>
              <h3
                style={{
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  color: '#ffffff',
                  marginBottom: '0.5rem',
                }}
              >
                防誤觸機制
              </h3>
              <p style={{ fontSize: '0.95rem', color: '#94a3b8', margin: 0, lineHeight: 1.6 }}>
                整合二次彈窗確認與動態倒數計時器，有效避免教職員在店家結帳前因誤觸而導致優惠券提前失效。
              </p>
            </div>

            <div style={styles.darkCard}>
              <div
                style={{
                  fontSize: '2.5rem',
                  fontWeight: 800,
                  color: '#a78bfa',
                  marginBottom: '0.5rem',
                }}
              >
                03
              </div>
              <h3
                style={{
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  color: '#ffffff',
                  marginBottom: '0.5rem',
                }}
              >
                防誤贈雙重防護
              </h3>
              <p style={{ fontSize: '0.95rem', color: '#94a3b8', margin: 0, lineHeight: 1.6 }}>
                在教職員同儕轉贈流程中，加入受贈者帳號身分查核與再次確認機制，防止因輸入錯誤而導致的不可逆誤贈。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Design Principles — Wide Container */}
      <section style={{ maxWidth: '1200px', margin: '0 auto 6rem', padding: '0 2rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h2
            style={{ fontSize: '2.25rem', fontWeight: 800, color: '#0f172a', marginTop: '0.4rem' }}
          >
            設計系統規範
          </h2>
        </div>

        <div
          className="yilan-principles-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
            gap: '1.5rem',
          }}
        >
          {PRINCIPLES_DATA.map((p, i) => (
            <div key={i} style={styles.principleCard}>
              <div style={styles.principleIconWrap}>{p.icon}</div>
              <div>
                <h3
                  style={{
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    color: '#0f172a',
                    margin: '0 0 0.35rem',
                  }}
                >
                  {p.title}
                </h3>
                <p style={{ fontSize: '0.925rem', color: '#64748b', margin: 0, lineHeight: 1.6 }}>
                  {p.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 6. Mobile Flow Showcase (Flows 01 to 05) */}
      <section
        className="yilan-fullbleed-section yilan-flow-section"
        style={{
          width: '100vw',
          marginLeft: 'calc(-50vw + 50%)',
          backgroundColor: '#f1f5f9',
          padding: '6rem 2rem',
        }}
      >
        <div style={{ maxWidth: '1250px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <span
              style={{
                fontSize: '0.85rem',
                fontWeight: 700,
                color: '#4f46e5',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              介面互動流程
            </span>
            <h2
              style={{
                fontSize: '2.25rem',
                fontWeight: 800,
                color: '#0f172a',
                marginTop: '0.4rem',
                lineHeight: 1.3,
              }}
            >
              從瀏覽、選擇、兌換到轉贈的全流程行動端介面
            </h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '5rem' }}>
            {FLOWS_DATA.map((flow) => (
              <div key={flow.id} className="yilan-flow-card" style={styles.flowSectionCard}>
                {/* Flow Header */}
                <div
                  className="yilan-flow-header"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: '1rem',
                    marginBottom: '2rem',
                    paddingBottom: '1.5rem',
                    borderBottom: '1px solid #e2e8f0',
                  }}
                >
                  <div>
                    <h3
                      style={{
                        fontSize: '1.6rem',
                        fontWeight: 800,
                        color: '#0f172a',
                        margin: '0 0 0.5rem',
                      }}
                    >
                      {flow.title}
                    </h3>
                    <p
                      style={{
                        fontSize: '1.05rem',
                        color: '#475569',
                        margin: 0,
                        maxWidth: '800px',
                        lineHeight: 1.6,
                      }}
                    >
                      {flow.desc}
                    </p>
                  </div>
                  <span style={styles.flowTagBadge}>{flow.tag}</span>
                </div>

                {/* Screen Mockups Grid */}
                <div
                  className="yilan-screens-scroll-container"
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '2rem',
                    flexWrap: 'wrap',
                    alignItems: 'flex-end',
                    padding: '1rem 0',
                  }}
                >
                  {flow.screens.map((screen, idx) => (
                    <div key={idx} style={styles.deviceMockup} className="yilan-device-mockup">
                      <div
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <img
                          src={screen.imgSrc}
                          alt={screen.label}
                          style={{ width: '100%', height: 'auto', display: 'block' }}
                        />
                      </div>
                      <div
                        style={{
                          padding: '0.85rem 0.5rem 0',
                          textAlign: 'center',
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          color: '#334155',
                        }}
                      >
                        {screen.label}
                      </div>
                    </div>
                  ))}
                </div>

                <div
                  style={{
                    textAlign: 'center',
                    marginTop: '1.5rem',
                    fontSize: '0.875rem',
                    color: '#94a3b8',
                    fontStyle: 'italic',
                  }}
                >
                  {flow.caption}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Results & Impact Section — Full Bleed Dark Finale */}
      <section
        className="yilan-fullbleed-section yilan-finale-section"
        style={{
          width: '100vw',
          marginLeft: 'calc(-50vw + 50%)',
          backgroundColor: '#090d16',
          color: '#ffffff',
          padding: '6rem 2rem',
          marginBottom: 0,
        }}
      >
        <div style={{ maxWidth: '1000px', margin: '0 auto', textAlign: 'center' }}>
          <span
            style={{
              fontSize: '0.85rem',
              fontWeight: 700,
              color: '#38bdf8',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              display: 'inline-block',
              marginBottom: '2.5rem',
            }}
          >
            成果回饋
          </span>

          <div
            style={{
              backgroundColor: 'rgba(30, 41, 59, 0.6)',
              borderLeft: '4px solid #38bdf8',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              borderRight: '1px solid rgba(255, 255, 255, 0.1)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '20px',
              padding: '3rem 2.5rem',
              textAlign: 'left',
              marginBottom: '3rem',
              backdropFilter: 'blur(10px)',
            }}
          >
            <p
              style={{
                fontSize: '1.25rem',
                fontStyle: 'italic',
                color: '#f8fafc',
                lineHeight: 1.8,
                margin: '0 0 1.5rem',
              }}
            >
              「自功能上線以來，優惠券模組顯著提升了 App
              的整體活躍度與使用率。教職員普遍反映狀態標示極為清晰、轉贈流程流暢；合作門市店家亦表示現場結帳延遲的狀況大幅減少。」
            </p>
            <div style={{ fontSize: '0.95rem', fontWeight: 800, color: '#38bdf8' }}>
              — 專案團隊與宜蘭縣教育平台團隊綜合反饋
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button
              onClick={() => router.push('/#work')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                backgroundColor: '#ffffff',
                color: '#0f172a',
                border: 'none',
                padding: '0.9rem 2.25rem',
                borderRadius: '9999px',
                fontSize: '1rem',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 10px 25px rgba(255, 255, 255, 0.15)',
                transition: 'all 0.2s ease',
              }}
            >
              <ArrowLeft size={18} />
              返回作品列表
            </button>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <Lightbox
        isOpen={lightboxOpen}
        images={lightboxImages}
        currentIndex={lightboxIndex}
        onClose={closeLightbox}
        onNext={nextImage}
        onPrev={prevImage}
      />
    </div>
  )
}

const styles = {
  darkCard: {
    backgroundColor: 'rgba(51, 65, 85, 0.5)',
    border: '1px solid rgba(255, 255, 255, 0.16)',
    borderRadius: '20px',
    padding: '2.25rem 2rem',
    backdropFilter: 'blur(10px)',
  },
  principleCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    padding: '1.75rem',
    display: 'flex',
    gap: '1.25rem',
    alignItems: 'flex-start',
    boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
  },
  principleIconWrap: {
    width: '46px',
    height: '46px',
    borderRadius: '12px',
    backgroundColor: '#fff7ed',
    border: '1px solid #ffedd5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  flowSectionCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '24px',
    padding: '2.5rem',
    boxShadow: '0 4px 25px rgba(0,0,0,0.04)',
  },
  flowTagBadge: {
    fontSize: '0.85rem',
    fontWeight: 800,
    color: '#ea580c',
    backgroundColor: '#fff7ed',
    border: '1px solid #ffedd5',
    padding: '6px 16px',
    borderRadius: '99px',
    whiteSpace: 'nowrap' as const,
  },
  deviceMockup: {
    width: '280px',
  },
}
